import React, { useState, useEffect } from 'react';
import { useOrg } from '../../hooks/useOrg';
import { useLocations } from '../../hooks/useLocations';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { api } from '../../lib/api';
import { pricePerLocation } from '../../lib/pipStates';
import AddressAutocomplete from '../shared/AddressAutocomplete';
import { AlertTriangle, X } from 'lucide-react';

const inputClass = 'w-full px-3.5 py-[11px] bg-tertiary border border-strong rounded-[10px] text-primary-c text-[13px] font-[inherit] transition-all focus:outline-none focus:border-[#0DA7CA] focus:shadow-[0_0_0_3px_rgba(13,167,202,0.15)]';

const TIMEZONES = [
  'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
  'America/Phoenix', 'America/Anchorage', 'Pacific/Honolulu',
];

const EMPTY_FORM = {
  name: '',
  address_line1: '',
  address_line2: '',
  city: '',
  state: '',
  zip: '',
  phone: '',
  timezone: 'America/New_York',
  is_primary: false,
};

// Shared modal used by the header "Add location" shortcut and the
// Settings → Locations tab. Pass `editing` = a location object to edit, or
// 'new' to create. Calls `onSaved` after a successful save (which also
// triggers a context refetch internally).
// Format cents → "$1,000". Falls back to a sensible default while the
// subscription context is still loading so the confirm screen never says
// "$undefined".
function fmtMoney(cents) {
  if (cents == null) return '$1,000';
  return (cents / 100).toLocaleString(undefined, { style: 'currency', currency: 'USD', minimumFractionDigits: 0 });
}

export default function LocationFormModal({ editing, onClose, onSaved }) {
  const { currentOrg } = useOrg();
  const { locations, refetchLocations } = useLocations();
  const { subscription, refresh: refreshSubscription } = useSubscription();
  const [form, setForm] = useState(EMPTY_FORM);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  // Two-step flow on create: form → confirm-pricing → save. Edits skip
  // confirmation since they don't change billing.
  const [step, setStep] = useState('form');
  const [billingMode, setBillingMode] = useState('free_until_referral'); // 065: new locations start free

  useEffect(() => {
    if (editing === 'new') {
      // First location auto-marked primary
      setForm({ ...EMPTY_FORM, is_primary: locations.length === 0 });
    } else if (editing && typeof editing === 'object') {
      setForm({
        name: editing.name || '',
        address_line1: editing.address_line1 || '',
        address_line2: editing.address_line2 || '',
        city: editing.city || '',
        state: editing.state || '',
        zip: editing.zip || '',
        phone: editing.phone || '',
        timezone: editing.timezone || 'America/New_York',
        is_primary: !!editing.is_primary,
      });
    }
    setError('');
    setStep('form');
  }, [editing, locations.length]);

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  // Mapbox autocomplete returns the full address. We only want the
  // street portion in line1, then fan out city/state/zip into their own
  // fields. The shared component already parses these for us.
  const onAddressSelect = (s) => {
    setForm(prev => ({
      ...prev,
      address_line1: s.address || s.full?.split(',')[0] || prev.address_line1,
      city: s.city || prev.city,
      state: s.state || prev.state,
      zip: s.zip || prev.zip,
    }));
  };

  // Law firms are free: no subscription, nothing billed per location, so the
  // pricing confirm step never shows for them (it quoted the medical-practice
  // rate and confused people).
  const billable = currentOrg?.type === 'medical_practice';

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    // Editing is free; new locations bump the bill and require confirm.
    if (editing === 'new' && billable) {
      setStep('confirm');
    } else {
      doSave();
    }
  };

  const doSave = async () => {
    setError('');
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        address_line1: form.address_line1 || null,
        address_line2: form.address_line2 || null,
        city: form.city || null,
        state: form.state || null,
        zip: form.zip || null,
        phone: form.phone || null,
        timezone: form.timezone || 'America/New_York',
        is_primary: !!form.is_primary,
        ...(editing === 'new' ? { billing_mode: billingMode } : {}),
      };
      let saved;
      if (editing === 'new') {
        saved = await api.createLocation(currentOrg.id, payload);
      } else {
        saved = await api.updateLocation(currentOrg.id, editing.id, payload);
      }
      await refetchLocations();
      // Bump the billing context so the BillingSettings page reflects
      // the new quantity/total without a manual refresh.
      if (editing === 'new') refreshSubscription?.();
      if (onSaved) onSaved(saved);
      onClose();
    } catch (err) {
      setError(err.message);
      setStep('form'); // back to form so they can retry/edit
    } finally {
      setSaving(false);
    }
  };

  if (!editing) return null;

  // Pricing breakdown for the confirm step. Pulls unit_amount from the
  // active subscription so it stays in sync if pricing ever changes. While the
  // subscription is still loading, fall back to the org's state-based rate
  // (PIP states bill higher) so the confirm screen never shows the wrong price.
  const unitAmount = subscription?.unit_amount ?? pricePerLocation(currentOrg?.state, currentOrg?.provider_type) * 100;
  const currentQty = subscription?.quantity ?? locations.length ?? 1;
  const newQty = currentQty + 1;
  const currentTotal = unitAmount != null ? unitAmount * currentQty : null;
  const newTotal = unitAmount != null ? unitAmount * newQty : null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center px-4 bg-overlay backdrop-blur-sm" onClick={onClose}>
      <div
        className="bg-elevated border border-theme rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-theme">
          <h3 className="text-[16px] font-bold text-primary-c">
            {step === 'confirm' ? 'Confirm new location' : (editing === 'new' ? 'Add location' : 'Edit location')}
          </h3>
          <button onClick={onClose} className="w-8 h-8 rounded-[8px] flex items-center justify-center text-muted-c hover:bg-hover-surface transition-all">
            <X size={16} strokeWidth={2.2} />
          </button>
        </div>

        {step === 'confirm' && (
          <div className="p-6 space-y-4">
            {error && (
              <div className="px-4 py-3 rounded-[10px] bg-danger-soft text-danger text-[13px] font-medium">{error}</div>
            )}

            <div className="space-y-2">
              {[
                ['free_until_referral', 'Free until it receives a case', `No charge now. The location joins your subscription at ${fmtMoney(unitAmount)}/month only when you accept a referral here.`],
                ['paid', 'Pay for this location now', `Adds ${fmtMoney(unitAmount)}/month immediately, prorated through the current billing period.`],
              ].map(([mode, title, desc]) => (
                <button key={mode} type="button" onClick={() => setBillingMode(mode)}
                  className={`w-full text-left rounded-[12px] border p-3.5 transition-all ${billingMode === mode ? 'border-accent-light bg-accent-glow' : 'border-theme bg-tertiary hover:bg-hover-surface'}`}>
                  <div className={`text-[13px] font-semibold ${billingMode === mode ? 'text-accent-light' : 'text-primary-c'}`}>{title}</div>
                  <div className="text-[11.5px] text-muted-c mt-0.5">{desc}</div>
                </button>
              ))}
            </div>

            <div className="rounded-[12px] border border-theme overflow-hidden">
              <div className="px-4 py-3 border-b border-theme bg-secondary">
                <div className="text-[12px] font-semibold text-primary-c">{form.name || 'New location'}</div>
                {(form.address_line1 || form.city) && (
                  <div className="text-[11px] text-muted-c truncate mt-0.5">
                    {[form.address_line1, form.city, form.state, form.zip].filter(Boolean).join(', ')}
                  </div>
                )}
              </div>
              <div className="p-4 space-y-2 text-[12px]">
                <div className="flex items-center justify-between">
                  <span className="text-muted-c">Paid locations today</span>
                  <span className="text-primary-c">{currentQty} × {fmtMoney(unitAmount)} = <span className="font-semibold">{currentTotal != null ? fmtMoney(currentTotal) : '—'}</span></span>
                </div>
                <div className="flex items-center justify-between pt-2 border-t border-theme">
                  <span className="text-secondary-c font-semibold">After adding this one</span>
                  <span className="text-primary-c font-bold">{billingMode === 'paid' ? (newTotal != null ? `${fmtMoney(newTotal)}/mo` : '—') : (currentTotal != null ? `${fmtMoney(currentTotal)}/mo (unchanged)` : '—')}</span>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-3 border-t border-theme">
              <button
                type="button"
                onClick={() => setStep('form')}
                disabled={saving}
                className="px-4 py-2 rounded-[10px] text-[13px] font-semibold text-secondary-c bg-tertiary border border-theme hover:bg-hover-surface transition-all"
              >
                Back
              </button>
              <button
                type="button"
                onClick={doSave}
                disabled={saving}
                className="px-4 py-2 rounded-[10px] text-[13px] font-semibold text-white gradient-accent shadow-glow hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:translate-y-0"
              >
                {saving ? 'Adding…' : billingMode === 'paid' ? `Add — +${fmtMoney(unitAmount)}/mo` : 'Add — free until a referral'}
              </button>
            </div>
          </div>
        )}

        {step === 'form' && (
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="px-4 py-3 rounded-[10px] bg-danger-soft text-danger text-[13px] font-medium">{error}</div>
          )}

          <div>
            <label className="block text-[12px] font-semibold text-secondary-c mb-2">Location name *</label>
            <input
              type="text"
              className={inputClass}
              value={form.name}
              onChange={e => update('name', e.target.value)}
              placeholder="e.g. Tampa Office"
              required
              autoFocus
            />
          </div>

          <div>
            <label className="block text-[12px] font-semibold text-secondary-c mb-2">Street address</label>
            <AddressAutocomplete
              value={form.address_line1}
              onChange={(val) => update('address_line1', val)}
              onSelect={onAddressSelect}
              placeholder="Start typing an address…"
              className={inputClass}
            />
          </div>
          <input type="text" className={inputClass} value={form.address_line2} onChange={e => update('address_line2', e.target.value)} placeholder="Suite, unit, etc." />

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-[12px] font-semibold text-secondary-c mb-2">City</label>
              <input type="text" className={inputClass} value={form.city} onChange={e => update('city', e.target.value)} />
            </div>
            <div>
              <label className="block text-[12px] font-semibold text-secondary-c mb-2">State</label>
              <input type="text" className={inputClass} value={form.state} onChange={e => update('state', e.target.value.toUpperCase())} maxLength={2} />
            </div>
            <div>
              <label className="block text-[12px] font-semibold text-secondary-c mb-2">ZIP</label>
              <input type="text" className={inputClass} value={form.zip} onChange={e => update('zip', e.target.value)} />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-[12px] font-semibold text-secondary-c mb-2">Phone</label>
              <input type="tel" className={inputClass} value={form.phone} onChange={e => update('phone', e.target.value)} />
            </div>
            <div>
              <label className="block text-[12px] font-semibold text-secondary-c mb-2">Timezone</label>
              <select className={inputClass} value={form.timezone} onChange={e => update('timezone', e.target.value)}>
                {TIMEZONES.map(tz => <option key={tz} value={tz}>{tz.replace(/_/g, ' ').replace('America/', '')}</option>)}
              </select>
            </div>
          </div>

          {!form.is_primary && (
            <label className="flex items-center gap-2 text-[12px] text-secondary-c cursor-pointer">
              <input
                type="checkbox"
                checked={form.is_primary}
                onChange={e => update('is_primary', e.target.checked)}
                className="w-4 h-4"
              />
              Set as primary location
            </label>
          )}
          {form.is_primary && editing !== 'new' && editing.is_primary && (
            <p className="text-[11px] text-muted-c">This is the primary location for the organization.</p>
          )}

          <div className="flex justify-end gap-2 pt-3 border-t border-theme">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-[10px] text-[13px] font-semibold text-secondary-c bg-tertiary border border-theme hover:bg-hover-surface transition-all">
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !form.name.trim()}
              className="px-4 py-2 rounded-[10px] text-[13px] font-semibold text-white gradient-accent shadow-glow hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:translate-y-0"
            >
              {editing === 'new' ? 'Continue' : (saving ? 'Saving…' : 'Save changes')}
            </button>
          </div>
        </form>
        )}
      </div>
    </div>
  );
}
